import { useCallback, useEffect, useRef, useState, type DragEvent } from 'react';
import { readDroppedFiles } from '../utils/dropEntries';

export interface UseFileDropOptions {
  /** Called with the dropped files once a drop is accepted. */
  onFiles: (files: File[]) => void;
  /** When true the zone ignores drags entirely and never reports active. */
  disabled?: boolean;
}

export interface UseFileDropResult {
  /** True while a file drag is hovering the zone — drive the highlight from this. */
  isDragActive: boolean;
  /** True while a dropped folder tree is being walked. */
  isReading: boolean;
  /** Spread onto the drop target element. */
  dropHandlers: {
    onDragEnter: (e: DragEvent<HTMLElement>) => void;
    onDragOver: (e: DragEvent<HTMLElement>) => void;
    onDragLeave: (e: DragEvent<HTMLElement>) => void;
    onDrop: (e: DragEvent<HTMLElement>) => void;
  };
}

/** True only for drags that actually carry files, not selected text or a URL. */
function isFileDrag(e: DragEvent<HTMLElement>): boolean {
  const types = e.dataTransfer?.types;
  if (!types) return false;
  // `types` is a DOMStringList in some browsers, so avoid Array.prototype.includes.
  return Array.from(types).includes('Files');
}

/**
 * Drag-and-drop plumbing for a file drop zone.
 *
 * Handles the three things that are easy to get wrong:
 *  - `preventDefault()` on dragenter *and* dragover, without which `drop` never fires;
 *  - a depth counter rather than a boolean for the highlight, since dragenter/
 *    dragleave fire for every child element the cursor crosses and a boolean
 *    makes the highlight flicker over the zone's own label text;
 *  - ignoring drags that carry no files, so dragging text over the page doesn't
 *    light up the zone or swallow the drop.
 *
 * Dropped folders are expanded to their contents — see `readDroppedFiles`.
 */
export function useFileDrop({ onFiles, disabled = false }: UseFileDropOptions): UseFileDropResult {
  const [isDragActive, setIsDragActive] = useState(false);
  const [isReading, setIsReading] = useState(false);
  const depthRef = useRef(0);
  // A folder walk outliving the card would set state on an unmounted component.
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const reset = useCallback(() => {
    depthRef.current = 0;
    setIsDragActive(false);
  }, []);

  const onDragEnter = useCallback(
    (e: DragEvent<HTMLElement>) => {
      if (disabled || !isFileDrag(e)) return;
      e.preventDefault();
      e.stopPropagation();
      depthRef.current += 1;
      setIsDragActive(true);
    },
    [disabled]
  );

  const onDragOver = useCallback(
    (e: DragEvent<HTMLElement>) => {
      if (disabled || !isFileDrag(e)) return;
      // Required for `drop` to fire at all.
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    },
    [disabled]
  );

  const onDragLeave = useCallback(
    (e: DragEvent<HTMLElement>) => {
      if (disabled || !isFileDrag(e)) return;
      e.preventDefault();
      e.stopPropagation();
      depthRef.current = Math.max(0, depthRef.current - 1);
      if (depthRef.current === 0) setIsDragActive(false);
    },
    [disabled]
  );

  const onDrop = useCallback(
    (e: DragEvent<HTMLElement>) => {
      if (disabled || !isFileDrag(e)) return;
      e.preventDefault();
      e.stopPropagation();
      reset();
      const dataTransfer = e.dataTransfer;
      if (!dataTransfer) return;

      setIsReading(true);
      // `readDroppedFiles` captures the item list synchronously before it is
      // neutered, so it is safe to hand it the event's DataTransfer here.
      readDroppedFiles(dataTransfer)
        .then((files) => {
          if (!mountedRef.current) return;
          if (files.length > 0) onFiles(files);
        })
        .finally(() => {
          if (mountedRef.current) setIsReading(false);
        });
    },
    [disabled, onFiles, reset]
  );

  return {
    isDragActive,
    isReading,
    dropHandlers: { onDragEnter, onDragOver, onDragLeave, onDrop },
  };
}
